// ======================================================================
// File Name       : ServiceAction
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import { decamelizeKeys, camelizeKeys } from 'humps';
import { SERVICE_SEARCH, SERVICE_RECEIVE_ENTERPRISE_STRUCTURE } from '../constants/ActionTypes';
import { commonCallApi } from '../utils/ApiUtils';
import { downloadFile } from '../utils/index';
import * as ServiceConstant from '../constants/ServiceConstant';
import * as EnterpriseAction from './EnterpriseAction';
import * as GroupAction from './GroupAction';
import * as UserAction from './UserAction';
import { SELECT_TARGET_DATA, STRUCTURE_LEVEL } from '../constants';
import * as enterpriseRequests from './httpRequests/enterprise';
import * as serviceRequest from './httpRequests/service';
import * as groupRequests from './httpRequests/group';
import * as userRequest from './httpRequests/user';
import * as userAttributeRequests from './httpRequests/userAttribute';
import * as serviceActionCreators from './actionCreators/service';
import { USER_ATTRIBUTE_TYPE } from '../constants/UserAttributeConstants';
import { RESET_SERVICE_NOTICE_DETAIL } from '../constants/actionTypes/service';
import { USER_CREATE_TYPE, USER_TYPE } from '../constants/UserConstants';

function receiveServiceList(serviceInfo) {
  return {
    type: SERVICE_SEARCH,
    serviceInfo,
  };
}

export function serviceSearch(searchInfo) {
  return async dispatch => commonCallApi({
    path: 'service/GetList',
    data: searchInfo,
    enterpriseId: 1,
    institutionId: 1,
  }).then((camelizeJson) => {
    if (searchInfo && searchInfo.selectTargetData === SELECT_TARGET_DATA.CSV) {
      const url = camelizeJson && camelizeJson.csvfileUrl ? camelizeJson.csvfileUrl : null;
      downloadFile(url);
    } else {
      dispatch(receiveServiceList(camelizeJson));
      return dispatch(
        getEnterpriseStructure({
          selectTargetData: SELECT_TARGET_DATA.DATA,
          recursiveType: STRUCTURE_LEVEL.ALL,
        }),
      );
    }
  });
}

export function serviceSearchWithoutGetEnterprise(searchInfo) {
  return (dispatch) => {
    commonCallApi({
      path: 'service/GetList',
      data: searchInfo,
      enterpriseId: 1,
      institutionId: 1,
    }).then((camelizeJson) => {
      dispatch(receiveServiceList(camelizeJson));
    });
  };
}

export function getServiceInfoAfterLogin(searchInfo) {
  return dispatch => commonCallApi({
    path: 'service/GetList',
    data: searchInfo,
    enterpriseId: 1,
    institutionId: 1,
  });
}

export function serviceEdit(param) {
  return dispatch => commonCallApi({
    path: 'service/Edit',
    data: param,
    enterpriseId: 1,
  }).then((camelizeJson) => {});
}

export function getEnterpriseStructure(param) {
  return dispatch =>
    enterpriseRequests.postEnterpriseGetStructure(param).then((apiRes) => {
      dispatch({
        type: SERVICE_RECEIVE_ENTERPRISE_STRUCTURE,
        enterpriseList: apiRes.enterpriseList,
      });
    });
}

export function getServiceNotificationList(param) {
  return async (dispatch) => {
    const res = await serviceRequest.getServiceNotificationList(param);
    return dispatch(serviceActionCreators.receiveServiceNotificationList(res));
  };
}

export function getServiceNoticeDetail(param, isEdit) {
  return async (dispatch, getState) => {
    await dispatch(UserAction.getGroupListAll());
    const groupInfo = getState().user.groupInfoAll;
    const res = await serviceRequest.getServiceNotificationList(param);
    let userListToSearch = [];
    let userListToStore = [];
    let groupRelationList = [];
    if (res && res.noticeList && res.noticeList.length) {
      const noticeDetail = res.noticeList[0];
      if (noticeDetail && noticeDetail.userList && noticeDetail.userList.length) {
        const { userType } = noticeDetail;
        const searchUserInfo = {
          userSecernIdList: noticeDetail.userList,
        };
        // Step 11.0.0 START
        if (userType === USER_TYPE.ADMIN) {
          searchUserInfo.userCreateType = USER_CREATE_TYPE.DEFINED_AND_REPRESENTATIVE_USER;
        }
        // Step 11.0.0 END
        userListToSearch = await userRequest.postUserGetList({ searchInfo: searchUserInfo });
        if (userListToSearch && userListToSearch.userList && userListToSearch.userList.length) {
          userListToStore = userListToSearch.userList;
          const userSecernIdList = userListToSearch.userList.reduce((acc, cur) => {
            if (cur.userSecernId) {
              acc.push(cur.userSecernId);
            }
            return acc;
          }, []);
          const searchGroupRelationInfo = { userList: userSecernIdList };
          const groupRelationRes = await groupRequests.postUserGroupRelationGetList({
            searchInfo: searchGroupRelationInfo,
          });
          if (groupRelationRes && groupRelationRes.groupRelationList) {
            groupRelationList = groupRelationRes.groupRelationList;
          }
        }
      }
    }
    if (isEdit) {
      return dispatch(
        serviceActionCreators.initServiceNoticeDetailForEdit(
          res,
          userListToStore,
          groupRelationList,
          groupInfo,
        ),
      );
    }
    return dispatch(
      serviceActionCreators.receiveServiceNoticeDetail(
        res,
        userListToStore,
        groupRelationList,
        groupInfo,
      ),
    );
  };
}

export function exportServiceNotificationList(param) {
  return async (dispatch) => {
    const res = await serviceRequest.getServiceNotificationList(param);
    if (res && res.csvfileUrl) {
      downloadFile(res.csvfileUrl);
    }
    return {};
  };
}

export function registerServiceNotification(param) {
  return async (dispatch) => {
    try {
      await serviceRequest.registerEditServiceNotification(param);
      return null;
    } catch (error) {
      return error;
    }
  };
}

export function deleteServiceNotification(param) {
  return async (dispatch) => {
    try {
      await serviceRequest.registerEditServiceNotification(param);
      return null;
    } catch (error) {
      return error;
    }
  };
}

export function addUsersByGroups(userType, groupList) {
  return async (dispatch, getState) => {
    let userListToAdd = [];
    let groupRelationList = [];
    let searchGroupRelationList = [];
    const groupInfo = getState().group.structures;
    const requests = [];
    let param = { userType };
    // Step 11.0.0 START
    if (userType === USER_TYPE.ADMIN) {
      param.userCreateType = USER_CREATE_TYPE.REPRESENTATIVE_USER;
    }
    // Step 11.0.0 END
    if (groupList && groupList.length) {
      for (const groupSecernId of groupList) {
        param = {
          userType,
          groupList: [groupSecernId],
        };
        // Step 11.0.0 START
        if (userType === USER_TYPE.ADMIN) {
          param.userCreateType = USER_CREATE_TYPE.REPRESENTATIVE_USER;
        }
        // Step 11.0.0 END
        requests.push(dispatch(getUserList(param)));
      }
    } else {
      requests.push(dispatch(getUserList(param)));
    }
    await Promise.all(requests);
    userListToAdd = getState().service.allUserByGroup;
    if (userListToAdd && userListToAdd.length) {
      searchGroupRelationList = userListToAdd.reduce((acc, cur) => {
        if (cur.userSecernId) {
          acc.push(cur.userSecernId);
        }
        return acc;
      }, []);
    }
    if (searchGroupRelationList && searchGroupRelationList.length) {
      const searchInfo = {};
      searchInfo.userList = searchGroupRelationList;
      const groupRelationListRes = await groupRequests.postUserGroupRelationGetList({
        searchInfo,
      });
      if (groupRelationListRes && groupRelationListRes.groupRelationList) {
        groupRelationList = groupRelationListRes.groupRelationList;
      }
    }
    return dispatch(
      serviceActionCreators.addUsersToListByGroups(userListToAdd, groupRelationList, groupInfo),
    );
  };
}

export function searchUserOnDialog({ paramSearchInfo, paramSearchAttribute }) {
  return async (dispatch, getState) => {
    let userSearchResult = { recordNum: 0, userList: [] };
    let groupRelationList = [];
    let userSecernIds = null;
    let groupInfo = getState().group.structures;
    if (!groupInfo || !groupInfo.bySecernId || !Object.keys(groupInfo.bySecernId).length) {
      await dispatch(GroupAction.fetchGroupStructure());
    }
    groupInfo = getState().group.structures;
    if (paramSearchAttribute && paramSearchAttribute.userAttributeSearchText) {
      const { userAttributeList } = await userAttributeRequests.postUserAttributeGetList({
        searchInfo: {
          userAttributeType: USER_ATTRIBUTE_TYPE.USER,
          userAttributeKeyNum: paramSearchAttribute.userAttributeKeyNum,
          userAttributeSearchText: paramSearchAttribute.userAttributeSearchText,
        },
      });
      if (!userAttributeList || !userAttributeList.length) {
        return dispatch(
          serviceActionCreators.storeUserList(userSearchResult, groupRelationList, groupInfo),
        );
      }
      userSecernIds = userAttributeList.reduce((acc, cur) => {
        if (cur.secernId) {
          acc.push(cur.secernId);
        }
        return acc;
      }, []);
    }

    if (userSecernIds && userSecernIds.length) {
      paramSearchInfo.userSecernIdList = userSecernIds;
    }
    const searchInfo = paramSearchInfo;
    const userSearchRes = await fetchUser(searchInfo);
    if (userSearchRes && userSearchRes.userList && userSearchRes.userList.length) {
      userSearchResult = userSearchRes;
    }
    if (userSearchResult && userSearchResult.userList && userSearchResult.userList.length) {
      const searchGroupRelationInfo = {};
      searchGroupRelationInfo.userList = userSearchResult.userList.reduce((acc, cur) => {
        if (cur.userSecernId) {
          acc.push(cur.userSecernId);
        }
        return acc;
      }, []);

      if (searchGroupRelationInfo.userList && searchGroupRelationInfo.userList.length) {
        const groupRelationListRes = await groupRequests.postUserGroupRelationGetList({
          searchInfo: searchGroupRelationInfo,
        });
        if (groupRelationListRes && groupRelationListRes.groupRelationList) {
          groupRelationList = groupRelationListRes.groupRelationList;
        }
      }
    }
    return dispatch(
      serviceActionCreators.storeUserList(userSearchResult, groupRelationList, groupInfo),
    );
  };
}

function fetchUser(searchInfo = {}) {
  return userRequest.postUserGetList({
    selectTargetData: SELECT_TARGET_DATA.DATA,
    searchInfo,
  });
}

export function addUsersToList(userListToAdd) {
  return async (dispatch) => {
    await dispatch(serviceActionCreators.addUsersToList(userListToAdd));
    return {};
  };
}

export function removeUserFromList(userSecernId) {
  return dispatch => dispatch(serviceActionCreators.removeUserFromList(userSecernId));
}

export function resetEdittingUsers() {
  return dispatch => dispatch(serviceActionCreators.resetEdittingUsers());
}

function getUserList(searchInfo = {}) {
  return async (dispatch) => {
    const result = await userRequest.postUserGetList({ searchInfo });
    return dispatch(serviceActionCreators.allUserReceived(result));
  };
}

export function removedAllUserByGroup() {
  return (dispatch) => {
    return dispatch(serviceActionCreators.removedAllUserByGroup());
  };
}

export function initializeVideoSpeedOptions(videoSpeedArr) {
  return (dispatch) => {
    return dispatch(serviceActionCreators.initializeVideoSpeedOptions(videoSpeedArr));
  };
}

export function updateVideoSpeedUsage(isUsed) {
  return (dispatch) => {
    return dispatch(serviceActionCreators.updateVideoSpeedUsage(isUsed));
  };
}

export function resetServiceNoticeDetail() {
  return {
    type: RESET_SERVICE_NOTICE_DETAIL,
  }
}